// Resolve which playlist a TV is REALLY playing right now based on its schedules
// and an `always_on` fallback. Returns the playlist object (from `playlists` list)
// or null if no enabled schedule slot covers the current minute AND no always_on
// playlist is assigned to this TV.
//
// This mirrors the backend logic in `core/tv_device.py::_resolve_active_playlist_id`
// so the admin UI matches what the Fire Stick is actually rendering.
// Time is anchored to America/New_York to match the backend resolver.
export function resolveActivePlaylist(tv, playlists /* timezone arg ignored — always NY */) {
  const schedules = tv?.schedules || [];

  // Walk schedules first (a scheduled slot always overrides always_on)
  if (schedules.length) {
    let weekday, minutes;
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(new Date());
      const wd = parts.find((p) => p.type === "weekday")?.value || "Mon";
      const h = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
      const m = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
      weekday = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 }[wd] ?? 0;
      minutes = (h % 24) * 60 + m;
    } catch {
      const d = new Date();
      weekday = (d.getDay() + 6) % 7;
      minutes = d.getHours() * 60 + d.getMinutes();
    }

    for (const s of schedules) {
      if (s.enabled === false) continue;
      const days = s.days_of_week || [];
      if (!days.includes(weekday)) continue;
      const start = Number(s.start_minute) || 0;
      const end = Number(s.end_minute) || 0;
      const inWindow =
        start <= end
          ? minutes >= start && minutes < end
          : minutes >= start || minutes < end;
      if (inWindow) {
        const pl = playlists.find((p) => p.id === s.playlist_id);
        if (pl) return pl;
      }
    }
  }

  // Fallback: an always_on playlist explicitly assigned to this TV (matches backend)
  const alwaysOn = playlists.find(
    (p) => p.always_on && p.business_id === tv.business_id && p.tv_id === tv.id
  );
  if (alwaysOn) return alwaysOn;

  // Or: an always_on playlist referenced in any of this TV's schedules
  const schedPids = new Set(
    (tv?.schedules || []).map((s) => s.playlist_id).filter(Boolean)
  );
  return (
    playlists.find(
      (p) => p.always_on && p.business_id === tv.business_id && schedPids.has(p.id)
    ) || null
  );
}
